"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.signedAdjustmentAmount = exports.DEDUCTIONS_EXCEED_INVOICE_BALANCE = void 0;
exports.sortInvoicesForFifoDiscount = sortInvoicesForFifoDiscount;
exports.proposeFifoDiscountAllocations = proposeFifoDiscountAllocations;
exports.allocatedDiscountForInvoice = allocatedDiscountForInvoice;
exports.invoiceAmountWithDiscountAllocations = invoiceAmountWithDiscountAllocations;
exports.normalizeAdjustmentAllocations = normalizeAdjustmentAllocations;
exports.allocationPlanForAdjustment = allocationPlanForAdjustment;
exports.totalAllocatedAmount = totalAllocatedAmount;
exports.invoiceRemainingBeforeProjectDiscounts = invoiceRemainingBeforeProjectDiscounts;
exports.isFullyDiscountedInvoiceAmount = isFullyDiscountedInvoiceAmount;
exports.validateAdjustmentAllocationPlans = validateAdjustmentAllocationPlans;
exports.ensureProjectDiscountAllocations = ensureProjectDiscountAllocations;
const round_money_js_1 = require("../money/round-money.js");
const revision_adjustment_code_js_1 = require("./revision-adjustment-code.js");
Object.defineProperty(exports, "signedAdjustmentAmount", { enumerable: true, get: function () { return revision_adjustment_code_js_1.signedAdjustmentAmount; } });
const revision_gating_js_1 = require("./revision-gating.js");
/** Unpaid first, then earliest due date; stable tie-break on invoice id. */
function sortInvoicesForFifoDiscount(invoices) {
    return [...invoices].sort((a, b) => {
        const aPaid = a.paid ? 1 : 0;
        const bPaid = b.paid ? 1 : 0;
        if (aPaid !== bPaid)
            return aPaid - bPaid;
        const aDue = (0, revision_gating_js_1.toDateOnly)(a.dueDate);
        const bDue = (0, revision_gating_js_1.toDateOnly)(b.dueDate);
        const aTime = aDue ? Date.parse(`${aDue}T00:00:00.000Z`) : Number.POSITIVE_INFINITY;
        const bTime = bDue ? Date.parse(`${bDue}T00:00:00.000Z`) : Number.POSITIVE_INFINITY;
        if (aTime !== bTime)
            return aTime - bTime;
        return a.invoiceId.localeCompare(b.invoiceId);
    });
}
/** Shared copy for FIFO / validate when deductions exceed remaining invoice capacity. */
exports.DEDUCTIONS_EXCEED_INVOICE_BALANCE = "Deductions cannot exceed the remaining invoice balance on this project";
/**
 * Propose a FIFO split of a project-level discount across invoice capacity.
 * Prefers unpaid invoices, then paid/funded remaining escrow capacity.
 * Amounts are positive; caller applies them as reductions.
 */
function proposeFifoDiscountAllocations(unpaidInvoices, discountAmount) {
    const target = (0, round_money_js_1.roundMoney)(Math.abs(Number(discountAmount || 0)));
    if (!(target > 0)) {
        return { ok: false, error: "Discount needs an amount" };
    }
    const sorted = sortInvoicesForFifoDiscount(unpaidInvoices).filter((invoice) => invoice.remaining > 0);
    let left = target;
    const allocations = [];
    for (const invoice of sorted) {
        if (left <= 1e-9)
            break;
        const take = (0, round_money_js_1.roundMoney)(Math.min(invoice.remaining, left));
        if (!(take > 0))
            continue;
        allocations.push({ invoiceId: invoice.invoiceId, amount: take });
        left = (0, round_money_js_1.roundMoney)(left - take);
    }
    // Absorb sub-cent remainder into the last tranche.
    if (left > 0 && left < 0.015 && allocations.length > 0) {
        const last = allocations[allocations.length - 1];
        last.amount = (0, round_money_js_1.roundMoney)(last.amount + left);
        left = 0;
    }
    if (left > 1e-9) {
        return { ok: false, error: exports.DEDUCTIONS_EXCEED_INVOICE_BALANCE };
    }
    return { ok: true, allocations };
}
function allocatedDiscountForInvoice(adjustmentAllocations, invoiceId, adjustmentId) {
    let sum = 0;
    for (const plan of adjustmentAllocations ?? []) {
        if (adjustmentId && plan.adjustmentId !== adjustmentId)
            continue;
        for (const row of plan.allocations) {
            if (row.invoiceId === invoiceId)
                sum += Math.abs(Number(row.amount || 0));
        }
    }
    return (0, round_money_js_1.roundMoney)(sum);
}
function invoiceAmountWithDiscountAllocations(milestones, adjustments, milestoneIds, adjustmentIds, invoiceId, adjustmentAllocations) {
    return (0, round_money_js_1.roundMoney)((0, revision_adjustment_code_js_1.sumAssignedInvoiceAmount)(milestones, adjustments, milestoneIds, adjustmentIds) -
        allocatedDiscountForInvoice(adjustmentAllocations, invoiceId));
}
function normalizeAdjustmentAllocations(raw) {
    if (!Array.isArray(raw))
        return [];
    const plans = [];
    for (const row of raw) {
        if (!row || typeof row !== "object" || Array.isArray(row))
            continue;
        const adjustmentId = row.adjustmentId;
        const allocationsRaw = row.allocations;
        if (typeof adjustmentId !== "string" || !adjustmentId.trim() || !Array.isArray(allocationsRaw))
            continue;
        const allocations = [];
        for (const item of allocationsRaw) {
            if (!item || typeof item !== "object" || Array.isArray(item))
                continue;
            const invoiceId = item.invoiceId;
            const amount = Number(item.amount || 0);
            if (typeof invoiceId !== "string" || !invoiceId.trim() || !(amount > 0))
                continue;
            allocations.push({ invoiceId, amount: (0, round_money_js_1.roundMoney)(amount) });
        }
        if (allocations.length) {
            plans.push({ adjustmentId, allocations });
        }
    }
    return plans;
}
function allocationPlanForAdjustment(meta, adjustmentId) {
    return (meta.adjustmentAllocations ?? []).find((plan) => plan.adjustmentId === adjustmentId);
}
function totalAllocatedAmount(plan) {
    if (!plan)
        return 0;
    return (0, round_money_js_1.roundMoney)(plan.allocations.reduce((sum, row) => sum + Math.abs(Number(row.amount || 0)), 0));
}
/** Remaining capacity after milestones + signed assigned adjustments (before project-discount FIFO). */
function invoiceRemainingBeforeProjectDiscounts(milestones, adjustments, milestoneIds, adjustmentIds) {
    return Math.max(0, (0, revision_adjustment_code_js_1.sumAssignedInvoiceAmount)(milestones, adjustments, milestoneIds, adjustmentIds));
}
/** True when an invoice net is fully covered by discounts (no client payment left). */
function isFullyDiscountedInvoiceAmount(amount) {
    return (0, round_money_js_1.roundMoney)(Number(amount || 0)) <= 0;
}
function validateAdjustmentAllocationPlans(adjustments, assignedAdjustmentIds, plans, capacities) {
    const errors = [];
    const remaining = new Map(capacities);
    const seen = new Set();
    for (const plan of plans) {
        if (seen.has(plan.adjustmentId)) {
            errors.push(`Discount ${plan.adjustmentId} has duplicate allocation plans`);
            continue;
        }
        seen.add(plan.adjustmentId);
        const adjustment = adjustments.find((item) => item.id === plan.adjustmentId);
        if (!adjustment) {
            errors.push(`Allocation references missing adjustment ${plan.adjustmentId}`);
            continue;
        }
        if (adjustment.kind !== "reduction") {
            errors.push(`Only deductions can use project discount allocations`);
            continue;
        }
        if (assignedAdjustmentIds.has(plan.adjustmentId)) {
            errors.push(`Deduction ${plan.adjustmentId} cannot be both invoice-attached and project-allocated`);
            continue;
        }
        const expected = (0, round_money_js_1.roundMoney)(Math.abs(Number(adjustment.amount || 0)));
        const allocated = totalAllocatedAmount(plan);
        if (Math.abs(allocated - expected) > 0.015) {
            errors.push(`Project discount allocations must equal the deduction amount`);
            continue;
        }
        for (const row of plan.allocations) {
            const capacity = remaining.get(row.invoiceId);
            if (capacity == null) {
                errors.push(`Project discount cannot target invoice ${row.invoiceId}`);
                continue;
            }
            if (row.amount - capacity > 1e-9) {
                errors.push(`Project discount exceeds remaining amount on invoice ${row.invoiceId}`);
                continue;
            }
            remaining.set(row.invoiceId, (0, round_money_js_1.roundMoney)(capacity - row.amount));
        }
    }
    return errors;
}
/**
 * Ensures every unattached reduction in the revision has a FIFO allocation plan.
 * Attached reductions are stripped from allocation plans.
 */
function ensureProjectDiscountAllocations(adjustments, revisionAdjustmentIds, assignedAdjustmentIds, unpaidCapacities, existingPlans = []) {
    const remaining = new Map(unpaidCapacities.map((invoice) => [invoice.invoiceId, invoice.remaining]));
    const dueById = new Map(unpaidCapacities.map((invoice) => [invoice.invoiceId, invoice.dueDate]));
    const paidById = new Map(unpaidCapacities.map((invoice) => [invoice.invoiceId, Boolean(invoice.paid)]));
    const plans = [];
    for (const adjustmentId of revisionAdjustmentIds) {
        const adjustment = adjustments.find((item) => item.id === adjustmentId);
        if (!adjustment || adjustment.kind !== "reduction")
            continue;
        if (assignedAdjustmentIds.has(adjustmentId))
            continue;
        const existing = existingPlans.find((plan) => plan.adjustmentId === adjustmentId);
        if (existing) {
            const expected = (0, round_money_js_1.roundMoney)(Math.abs(Number(adjustment.amount || 0)));
            if (Math.abs(totalAllocatedAmount(existing) - expected) <= 0.015) {
                const capacityErrors = validateAdjustmentAllocationPlans(adjustments, assignedAdjustmentIds, [existing], remaining);
                if (capacityErrors.length === 0) {
                    for (const row of existing.allocations) {
                        remaining.set(row.invoiceId, (0, round_money_js_1.roundMoney)((remaining.get(row.invoiceId) ?? 0) - row.amount));
                    }
                    plans.push(existing);
                    continue;
                }
            }
        }
        const proposed = proposeFifoDiscountAllocations([...remaining.entries()].map(([invoiceId, rem]) => ({
            invoiceId,
            dueDate: dueById.get(invoiceId),
            remaining: rem,
            paid: paidById.get(invoiceId),
        })), adjustment.amount ?? 0);
        if (proposed.ok === false) {
            return { ok: false, error: proposed.error, plans };
        }
        for (const row of proposed.allocations) {
            remaining.set(row.invoiceId, (0, round_money_js_1.roundMoney)((remaining.get(row.invoiceId) ?? 0) - row.amount));
        }
        plans.push({ adjustmentId, allocations: proposed.allocations });
    }
    return { ok: true, plans };
}
